<template>
  <main class="flex w-full bg-[#f1f3ff]">
    <!-- <LeftNav class="m-0" /> -->
    <section
      class="flex flex-col overflow-hidden w-full transition-all duration-300"
    >
     <Header />
      <!-- <TopNav /> -->
      <div
        class="bg-[#F9FAFF] p-6 h-[calc(100vh-100px)] overflow-auto mx-3 rounded-[30px] flex flex-col gap-3 2xl:gap-5 page-wrapper"
      >
        <div class="py-3 px-1">
          <!-- Loading -->
          <div v-if="loading" class="flex justify-center items-center py-24">
            <svg
              class="animate-spin h-8 w-8 text-[#76bdeb]"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8H4z"
              ></path>
            </svg>
          </div>

          <!-- Error -->
          <div v-else-if="error" class="text-center py-16 text-red-500 text-sm">
            {{ error }}
          </div>

          <template v-else-if="ticket">
            <!-- Back + Header bar -->
            <div class="flex items-center gap-3 mb-4">
              <button
                @click="$router.back()"
                class="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="2"
                  stroke="currentColor"
                  class="w-4 h-4"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
                  />
                </svg>
                Back
              </button>
              <span class="text-gray-300">|</span>
              <span class="text-sm text-gray-400">Tickets</span>
              <span class="text-gray-300">/</span>
              <span class="text-sm font-medium text-[#76bdeb]">{{
                ticket.ticket_number
              }}</span>
            </div>

            <!-- Main card -->
            <div
              class="bg-white rounded-xl shadow-lg overflow-hidden text-start"
            >
              <!-- Ticket header -->
              <div class="bg_primary px-6 py-5 text-black">
                <div class="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p class="text-xs opacity-75 mb-1">
                      {{ ticket.ticket_number }}
                    </p>
                    <h1 class="text-lg font-semibold leading-snug max-w-2xl">
                      {{ ticket.title }}
                    </h1>
                  </div>
                  <div class="flex gap-2 flex-wrap">
                    <span
                      :class="priorityClass(ticket.priority)"
                      class="px-3 py-1 rounded-full text-xs font-semibold capitalize"
                    >
                      {{ ticket.priority }}
                    </span>
                    <span
                      :class="typeClass(ticket.ticket_type)"
                      class="px-3 py-1 rounded-full text-xs font-semibold"
                    >
                      {{ typeLabel(ticket.ticket_type) }}
                    </span>
                    <span
                      :class="statusClass(ticket.status)"
                      class="px-3 py-1 rounded-full text-xs font-semibold"
                    >
                      {{ statusLabel(ticket.status) }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="p-6 flex flex-col gap-6">
                <!-- Info grid -->
                <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div class="flex flex-col gap-1">
                    <p class="text-xs text-gray-400 uppercase font-semibold">
                      Raised By
                    </p>
                    <p class="text-sm text-gray-700">
                      {{ ticket.raised_by || "—" }}
                    </p>
                  </div>
                  <div class="flex flex-col gap-1">
                    <p class="text-xs text-gray-400 uppercase font-semibold">
                      Module
                    </p>
                    <p class="text-sm text-gray-700">
                      {{ ticket.module || "—" }}
                    </p>
                  </div>
                  <div class="flex flex-col gap-1">
                    <p class="text-xs text-gray-400 uppercase font-semibold">
                      Project
                    </p>
                    <p class="text-sm text-gray-700">
                      {{ ticket.project_name || "—" }}
                    </p>
                  </div>
                  <div class="flex flex-col gap-1">
                    <p class="text-xs text-gray-400 uppercase font-semibold">
                      Project Code
                    </p>
                    <p class="text-sm text-gray-700">
                      {{ ticket.project_code || "—" }}
                    </p>
                  </div>
                  <div class="flex flex-col gap-1">
                    <p class="text-xs text-gray-400 uppercase font-semibold">
                      Created
                    </p>
                    <p class="text-sm text-gray-700">
                      {{ formatDate(ticket.created_at) }}
                    </p>
                  </div>
                  <div class="flex flex-col gap-1">
                    <p class="text-xs text-gray-400 uppercase font-semibold">
                      Updated
                    </p>
                    <p class="text-sm text-gray-700">
                      {{ formatDate(ticket.updated_at) }}
                    </p>
                  </div>
                  <div class="flex flex-col gap-1">
                    <p class="text-xs text-gray-400 uppercase font-semibold">
                      Resolved
                    </p>
                    <p class="text-sm text-gray-700">
                      {{ formatDate(ticket.resolved_at) }}
                    </p>
                  </div>
                  <div v-if="ticket.affected_url" class="flex flex-col gap-1">
                    <p class="text-xs text-gray-400 uppercase font-semibold">
                      Affected URL
                    </p>
                    <a
                      :href="ticket.affected_url"
                      target="_blank"
                      class="text-sm text-[#76bdeb] truncate hover:underline"
                    >
                      {{ ticket.affected_url }}
                    </a>
                  </div>
                </div>

                <hr class="border-gray-100" />

                <!-- Description -->
                <div>
                  <p class="text-xs text-gray-400 uppercase font-semibold mb-2">
                    Description
                  </p>
                  <p
                    class="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed"
                  >
                    {{ ticket.description }}
                  </p>
                </div>

                <!-- Bug-specific fields -->
                <template v-if="ticket.ticket_type === 'bug'">
                  <hr class="border-gray-100" />
                  <div
                    v-if="ticket.steps_to_reproduce"
                    class="flex flex-col gap-2"
                  >
                    <p class="text-xs text-gray-400 uppercase font-semibold">
                      Steps to Reproduce
                    </p>
                    <p
                      class="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed bg-gray-50 rounded-lg p-4"
                    >
                      {{ ticket.steps_to_reproduce }}
                    </p>
                  </div>
                  <div
                    v-if="ticket.expected_behavior || ticket.actual_behavior"
                    class="grid grid-cols-1 md:grid-cols-2 gap-4"
                  >
                    <div v-if="ticket.expected_behavior">
                      <p
                        class="text-xs text-gray-400 uppercase font-semibold mb-2"
                      >
                        Expected Behavior
                      </p>
                      <p
                        class="text-sm text-gray-700 whitespace-pre-wrap bg-green-50 rounded-lg p-4 leading-relaxed"
                      >
                        {{ ticket.expected_behavior }}
                      </p>
                    </div>
                    <div v-if="ticket.actual_behavior">
                      <p
                        class="text-xs text-gray-400 uppercase font-semibold mb-2"
                      >
                        Actual Behavior
                      </p>
                      <p
                        class="text-sm text-gray-700 whitespace-pre-wrap bg-red-50 rounded-lg p-4 leading-relaxed"
                      >
                        {{ ticket.actual_behavior }}
                      </p>
                    </div>
                  </div>
                </template>

                <!-- Attachments -->
                <template v-if="attachments.length">
                  <hr class="border-gray-100" />
                  <div>
                    <p
                      class="text-xs text-gray-400 uppercase font-semibold mb-3"
                    >
                      Attachments ({{ attachments.length }})
                    </p>
                    <div class="flex flex-wrap gap-3">
                      <a
                        v-for="att in attachments"
                        :key="att.id"
                        :href="att.file"
                        target="_blank"
                        class="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-700 hover:bg-gray-100 transition-colors"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke-width="1.5"
                          stroke="currentColor"
                          class="w-4 h-4 text-gray-400"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13"
                          />
                        </svg>
                        <span class="max-w-[160px] truncate">{{
                          att.filename
                        }}</span>
                      </a>
                    </div>
                  </div>
                </template>

                <!-- Comments -->
                <hr class="border-gray-100" />
                <div class="flex flex-col gap-4">
                  <p class="text-xs text-gray-400 uppercase font-semibold">
                    Comments ({{ comments.length }})
                  </p>

                  <!-- Comment list -->
                  <div
                    v-if="comments.length === 0"
                    class="text-sm text-gray-400 italic"
                  >
                    No comments yet. Be the first to comment.
                  </div>
                  <div v-else class="flex flex-col gap-3">
                    <div
                      v-for="comment in comments"
                      :key="comment.id"
                      :class="
                        comment.is_internal
                          ? 'bg-yellow-50 border-yellow-200'
                          : 'bg-gray-50 border-gray-100'
                      "
                      class="rounded-lg p-4 border"
                    >
                      <div class="flex justify-between items-start mb-2">
                        <div class="flex items-center gap-2">
                          <div
                            class="w-7 h-7 rounded-full bg_primary flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                          >
                            {{
                              (comment.author || "?").charAt(0).toUpperCase()
                            }}
                          </div>
                          <span class="text-xs font-medium text-gray-700">{{
                            comment.author || "Unknown"
                          }}</span>
                        </div>
                        <div class="flex items-center gap-2">
                          <span
                            v-if="comment.is_internal"
                            class="text-[10px] px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full font-medium"
                            >Internal</span
                          >
                          <span class="text-xs text-gray-400">{{
                            formatDate(comment.created_at)
                          }}</span>
                        </div>
                      </div>
                      <p
                        class="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed pl-9"
                      >
                        {{ comment.body }}
                      </p>
                    </div>
                  </div>

                  <!-- Add comment form -->
                  <div class="border border-gray-200 rounded-xl p-4 bg-gray-50">
                    <p
                      class="text-xs font-semibold text-gray-500 uppercase mb-3"
                    >
                      Add a Comment
                    </p>

                    <!-- Flash -->
                    <div
                      v-if="commentFlash.text"
                      :class="
                        commentFlash.type === 'success'
                          ? 'bg-green-50 border-green-300 text-green-700'
                          : 'bg-red-50 border-red-300 text-red-700'
                      "
                      class="border rounded-md px-3 py-2 text-xs mb-3"
                    >
                      {{ commentFlash.text }}
                    </div>

                    <textarea
                      v-model="newComment.body"
                      rows="3"
                      placeholder="Write your comment here..."
                      class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#76bdeb] resize-none bg-white"
                    ></textarea>

                    <div
                      class="flex items-center justify-between mt-3 flex-wrap gap-3"
                    >
                      <!-- Internal toggle -->
                      <label
                        class="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none"
                      >
                        <div
                          @click="
                            newComment.is_internal = !newComment.is_internal
                          "
                          :class="
                            newComment.is_internal
                              ? 'bg_primary'
                              : 'bg-gray-300'
                          "
                          class="w-9 h-5 rounded-full flex items-center transition-colors duration-200 px-0.5 cursor-pointer"
                        >
                          <div
                            :class="
                              newComment.is_internal
                                ? 'translate-x-4'
                                : 'translate-x-0'
                            "
                            class="w-4 h-4 bg-white rounded-full shadow transition-transform duration-200"
                          ></div>
                        </div>
                        Mark as Internal Note
                      </label>

                      <!-- Submit -->
                      <button
                        @click="postComment"
                        :disabled="commentLoading || !newComment.body.trim()"
                        class="flex items-center gap-2 px-5 py-2 text-sm rounded-lg bg_primary text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                      >
                        <svg
                          v-if="commentLoading"
                          class="animate-spin h-4 w-4"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            class="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            stroke-width="4"
                          ></circle>
                          <path
                            class="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8H4z"
                          ></path>
                        </svg>
                        {{ commentLoading ? "Posting..." : "Post Comment" }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import axios from "axios";
import { mapGetters } from "vuex";
import Header from "@/components/Admin/Header.vue";

// import LeftNav from "@/components/LeftNav.vue";
// import TopNav from "@/components/TopNav.vue";

const ECOAUTH_TOKEN =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJkb21haW4iOiJwbXMuY2hhZHVyYS5jb20iLCJ1c2VyX2lkIjo2LCJleHAiOjE4NzQwNTA5NDZ9._M_j0Xe6wxhT5ckdrQsf9BLhcCblw1BesfQEKb4X8R4";

const HEADERS = { ecoauth: ECOAUTH_TOKEN };

export default {
  name: "TicketDetail",
  components: {
    // LeftNav,
    // TopNav,
    Header,
  },
  props: {
    id: { type: [String, Number], required: true },
  },
  data() {
    return {
      ticket: null,
      attachments: [],
      comments: [],
      loading: false,
      error: "",
      newComment: { body: "", is_internal: false },
      commentLoading: false,
      commentFlash: { text: "", type: "" },
    };
  },
  computed: {
    ...mapGetters(["getUserInfo", "userInfo"]),
  },
  created() {
    this.fetchAll();
  },
  methods: {
    async fetchAll() {
      this.loading = true;
      this.error = "";
      try {
        const [ticketRes, attRes, commentRes] = await Promise.all([
          axios.get(`${process.env.VUE_APP_TICKET_URL}/tickets/${this.id}/`, {
            headers: HEADERS,
          }),
          axios.get(
            `${process.env.VUE_APP_TICKET_URL}/ticket-attachments/list/`,
            {
              headers: HEADERS,
            },
          ),
          axios.get(`${process.env.VUE_APP_TICKET_URL}/ticket-comments/list/`, {
            headers: HEADERS,
          }),
        ]);

        this.ticket = ticketRes.data?.data ?? ticketRes.data;
        this.attachments = (attRes.data?.data ?? []).filter(
          (a) => a.ticket === Number(this.id),
        );
        this.comments = (commentRes.data?.data ?? []).filter(
          (c) => c.ticket === Number(this.id),
        );
      } catch (err) {
        this.error = "Failed to load ticket details. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return "—";
      return new Date(dateStr).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    typeLabel(type) {
      return (
        {
          bug: "Bug / Issue",
          feature: "Feature Request",
          support: "General Support",
          other: "Other",
        }[type] || type
      );
    },
    statusLabel(status) {
      return (
        {
          open: "Open",
          in_progress: "In Progress",
          awaiting_client: "Awaiting Client",
          resolved: "Resolved",
          closed: "Closed",
        }[status] || status
      );
    },
    priorityClass(p) {
      return (
        {
          low: "bg-blues bg-opacity-20 text-white border border-white border-opacity-30",
          medium: "bg-blue-500 bg-opacity-30 text-white",
          high: "bg-orange-400 bg-opacity-40 text-white",
          critical: "bg-red-500 bg-opacity-40 text-white",
        }[p] || ""
      );
    },
    typeClass(t) {
      return (
        {
          bug: "bg-red-400 bg-opacity-30 text-white",
          feature: "bg-indigo-400 bg-opacity-30 text-white",
          support: "bg-teal-400 bg-opacity-30 text-white",
          other: "bg-blues bg-opacity-20 text-white",
        }[t] || ""
      );
    },
    statusClass(s) {
      return (
        {
          open: "bg-blue-600 bg-opacity-30 text-white",
          in_progress: "bg-blue-300 bg-opacity-30 text-white",
          awaiting_client: "bg-purple-400 bg-opacity-30 text-white",
          resolved: "bg-green-400 bg-opacity-30 text-white",
          closed: "bg-white bg-opacity-20 text-white",
        }[s] || ""
      );
    },
    async postComment() {
      if (!this.newComment.body.trim()) return;
      this.commentLoading = true;
      this.commentFlash = { text: "", type: "" };
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_TICKET_URL}/tickets/comments/create/`,
          {
            ticket: Number(this.id),
            author: this.userInfo?.user?.email || "",
            body: this.newComment.body,
            is_internal: this.newComment.is_internal,
          },
          { headers: HEADERS },
        );
        if (response.data.success) {
          this.comments.push(response.data.data);
          this.newComment = { body: "", is_internal: false };
          this.commentFlash = {
            text: "Comment posted successfully!",
            type: "success",
          };
          setTimeout(() => {
            this.commentFlash = { text: "", type: "" };
          }, 3000);
        } else {
          this.commentFlash = {
            text: response.data.message || "Failed to post comment.",
            type: "error",
          };
        }
      } catch (err) {
        this.commentFlash = {
          text: "Something went wrong. Please try again.",
          type: "error",
        };
      } finally {
        this.commentLoading = false;
      }
    },
  },
};
</script>
